import { ScheduleBottom, ScheduleIntroductionContents, ScheduleTopmost } from "routes/schedule/scheduleIntroduction"
import { AdditionalInfo } from "routes/regime/components/additional-info"
import { AdditionalNotes } from "routes/regime/components/additional-notes"
import { InlineIntroOverride } from "routes/regime/components/inline-intro-override"
import { InlineTaskContentOverride } from "routes/regime/components/inline-task-content-override"
import { InlineTaskNoteOverride } from "routes/regime/components/inline-task-note-override"
import { InlineTaskTitleOverride } from "routes/regime/components/inline-task-title-override"
import {
    showIntroOverride,
    showTaskContentOverride,
    showTaskNoteOverride,
    showTaskTitleOverride,
} from "routes/regime/components/content-override-gates"
import { Pictures } from "routes/regime/extension-edit/pictures"
import {
    ClassificationArea,
    ScheduleSummaryAdaptor,
    SkillSetArea,
    TaskContent,
    TaskNotes,
    TaskRowStart,
    TaskTitle,
    TimeAndFrequency,
} from "slot-definitions"
import { Included } from "routes/regime/components/included"
import { EditTimings } from "routes/regime/components/edit-timings"
import { EditSkills } from "routes/regime/components/edit-skills"
import { EditClassification } from "routes/regime/components/edit-classification"
import { ZeroU } from "routes/regime/extension-edit/zero-u"
import { PayloadTask } from "routes/regime/components/payload-task"
import { ContextMenu } from "lib/@components/context-menu-bar"
import { SCHEDULE } from "library/constants.mjs"
import { ContextMenuItem } from "lib/@components/context-menu-bar/context-menu-item"
import { RefreshRouter } from "event-definitions"
import { cleanObject } from "lib/clean-object"
import { replaceAllWith } from "lib/@components/slot/replace-all-with"
import { ExtendSummarySchedule } from "routes/regime/extension-edit/extend-summary-schedule"
import { Payload } from "routes/schedule/controller/payload"
import { isGlobalTailoringId } from "library/get-unique-id-from-tree.mjs"
import { Inventory } from "../assets/inventory"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { ScheduleAssetsTable } from "../assets/schedule-assets-table"
import { Bound } from "lib/@components/binding/Bound"
import { Box } from "@mui/material"
import { InventoryCard } from "../assets/inventory-card"
import { RegimeDraftLockHide } from "../regime-draft-locking/regime-draft-lock-hide"
import { createEvent } from "library/local-events"

export const RemoveAllCustomisations = createEvent("RemoveAllCustomisations")

// Preload the Jodit editor chunk so it is available when the user clicks
// "Add Introduction" or "Add Notes" while offline.  In the tailoring flow
// the default BoundEditableRichText (which would normally load the chunk)
// is replaced by the AdditionalInfo/AdditionalNotes slot plugs, so the
// chunk would otherwise only be requested on button click.
import("minimals-template/components/editor/jodit-editor").catch(() => {})

export function ExtensionEdit({ children }) {
    const { payload } = useBoundContext()
    const isSynced = !!payload?.assetRegisters?.length || !!payload?.register

    const scheduleInventory = isSynced ? (
        <Bound target={payload.units}>
            <Box my={2} mx={0.5}>
                <InventoryCard>
                    <ScheduleAssetsTable />
                </InventoryCard>
            </Box>
        </Bound>
    ) : (
        <Inventory />
    )

    ScheduleIntroductionContents.usePlug(
        <Payload priority={10}>
            <AdditionalInfo />
        </Payload>
    )
    ScheduleIntroductionContents.usePlug(
        <Payload priority={200}>
            <AdditionalNotes />
        </Payload>
    )
    // Collaboration clients edit intro body + note inline (replacing the read-only display and the
    // prepend buttons above). With the rollout flag off the gate still fires when a stored override
    // exists, so the override stays visible (read-only) rather than reverting to the master text here
    // while the schedule view keeps showing it (FIQ-1631). Non-collab tailoring keeps the stock plugs.
    ScheduleIntroductionContents.usePlug(replaceAllWith(<InlineIntroOverride if={showIntroOverride} />))
    ScheduleSummaryAdaptor.usePlug(<ExtendSummarySchedule />)
    ScheduleTopmost.usePlug(
        <Payload priority={1} if={({ currentId }) => !isGlobalTailoringId(currentId, true)}>
            {scheduleInventory}
        </Payload>,
        [isSynced]
    )
    ScheduleBottom.usePlug(
        <Payload priority={10}>
            <RegimeDraftLockHide>
                <Pictures />
            </RegimeDraftLockHide>
        </Payload>
    )
    TaskRowStart.usePlug(({ context }) => !context.target.isHeader && <Included priority={1} />)
    TimeAndFrequency.usePlug(replaceAllWith(<EditTimings />))
    SkillSetArea.usePlug(replaceAllWith(<EditSkills />))

    ClassificationArea.usePlug(replaceAllWith(<EditClassification />))
    TimeAndFrequency.usePlug(
        <Payload priority={10}>
            <ZeroU />
        </Payload>
    )
    TaskContent.usePlug(
        <PayloadTask priority={1}>
            <AdditionalInfo />
        </PayloadTask>
    )
    // Collaboration clients edit the task body inline; non-collab tailoring keeps the stock content.
    // Flag off + stored override = read-only override display (see content-override-gates.js).
    TaskContent.usePlug(replaceAllWith(<InlineTaskContentOverride if={showTaskContentOverride} />))
    TaskNotes.usePlug(
        <PayloadTask priority={200}>
            <AdditionalNotes />
        </PayloadTask>
    )
    // Collaboration clients edit the existing task note inline; non-collab tailoring keeps the stock
    // "Add notes" prepend. Flag off + stored override = read-only override display.
    TaskNotes.usePlug(replaceAllWith(<InlineTaskNoteOverride if={showTaskNoteOverride} />))
    // Collaboration clients edit the master task title inline; non-collab tailoring keeps the read-only title.
    // Flag off + stored override = read-only override display.
    TaskTitle.usePlug(replaceAllWith(<InlineTaskTitleOverride if={showTaskTitleOverride} />))

    ContextMenu(SCHEDULE).usePlug(({ context }) => (
        <RegimeDraftLockHide>
            <ContextMenuItem
                icon="fluent:delete-12-filled"
                label="Remove all customisations"
                onClick={() => {
                    removeAll(context.payload, context.storePayload, context.tailoringReferenceId)
                }}
            />
        </RegimeDraftLockHide>
    ))

    return children

    function removeAll(payload, storePayload, tailoringReferenceId) {
        cleanObject(payload)
        storePayload()
        RefreshRouter.raiseOnce()
        RemoveAllCustomisations.raiseOnce({ tailoringReferenceId })
    }
}
